import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Calculator, TrendingUp, DollarSign, Clock, Users, ChevronRight, ChevronLeft, Check, X, Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";

// ROI Calculator Webhook configuration (exclusive variables, no fallbacks)
const WEBHOOK_URL = import.meta.env.VITE_ROI_WEBHOOK_URL;
const WEBHOOK_USERNAME = import.meta.env.VITE_ROI_WEBHOOK_USER;
const WEBHOOK_PASSWORD = import.meta.env.VITE_ROI_WEBHOOK_PASS;

interface ROICalculatorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ROICalculator = ({ open, onOpenChange }: ROICalculatorProps) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [employees, setEmployees] = useState<number>(100);
  const [ticketsPerMonth, setTicketsPerMonth] = useState<number>(500);
  const [avgHandlingTime, setAvgHandlingTime] = useState<number>(30);
  const [costPerHour, setCostPerHour] = useState<number>(125);
  const [analystLicenseCost, setAnalystLicenseCost] = useState<number>(0);
  const [isOnPremises, setIsOnPremises] = useState<boolean>(false);
  const [showEmailForm, setShowEmailForm] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  
  // Estados do formulário de email
  const [emailForm, setEmailForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    companySize: ''
  });
  
  // Estados para funcionalidades da segunda página
  const [userFeatures, setUserFeatures] = useState({
    incidents: false,
    requests: false,
    problems: false,
    changes: false,
    assetManagement: false,
    knowledgeBase: false,
    reports: false,
    forms: false,
    slaManagement: false
  });

  // Estados para módulos AROAN da terceira página
  const [selectedModules, setSelectedModules] = useState({
    incidents: false,
    requests: false,
    problems: false,
    changes: false,
    assetManagement: false,
    knowledgeBase: false,
    reports: false,
    forms: false,
    slaManagement: false,
    aiIntegrated: false
  });

  // Cálculos de ROI
  const currentMonthlyHours = (ticketsPerMonth * avgHandlingTime) / 60;
  const currentMonthlyCost = currentMonthlyHours * costPerHour;
  const currentAnnualCost = currentMonthlyCost * 12;

  // Custo mensal das licenças de analista
  const monthlyAnalystLicenseCost = analystLicenseCost * employees;
  
  // Custo mensal de on-premises (R$ 100.000 por ano = R$ 8.333,33 por mês)
  const monthlyOnPremisesCost = isOnPremises ? 100000 / 12 : 0;
  
  // Custo total atual por mês (incluindo licenças de analista e on-premises)
  const totalCurrentMonthlyCost = currentMonthlyCost + monthlyAnalystLicenseCost + monthlyOnPremisesCost;

  // Com AROAN (80% de redução no tempo de atendimento)
  const newMonthlyHours = currentMonthlyHours * 0.2;
  const newMonthlyCost = newMonthlyHours * costPerHour;
  const newAnnualCost = newMonthlyCost * 12;

  const monthlySavings = currentMonthlyCost - newMonthlyCost;
  const annualSavings = currentAnnualCost - newAnnualCost;

  // Preços dos módulos AROAN (por licença unitária)
  const modulesPricing = {
    incidents: 50,
    requests: 60,
    problems: 50,
    changes: 150,
    assetManagement: 70,
    knowledgeBase: 50,
    reports: 0,
    forms: 120,
    slaManagement: 0,
    aiIntegrated: 6500
  };

  // Cálculo do custo dos módulos selecionados
  const calculateModuleCost = () => {
    const selectedModuleKeys = Object.keys(selectedModules).filter(
      key => selectedModules[key as keyof typeof selectedModules]
    );
    
    if (selectedModuleKeys.length === 0) return 0;
    
    const totalModuleCost = selectedModuleKeys.reduce((total, moduleKey) => {
      return total + modulesPricing[moduleKey as keyof typeof modulesPricing];
    }, 0);
    
    return totalModuleCost;
  };

  const moduleUnitCost = calculateModuleCost();
  
  // Custo estimado da plataforma (ajustado para on-premises e módulos selecionados)
  const basePlatformCost = isOnPremises ? 350 : 475;
  const monthlyPlatformCost = employees * (moduleUnitCost > 0 ? moduleUnitCost : basePlatformCost);
  const annualPlatformCost = monthlyPlatformCost * 12;

  // Custo anual das licenças de analista
  const annualAnalystLicenseCost = analystLicenseCost * employees * 12;
  
  // Custo total incluindo licenças de analista
  const totalAnnualCost = annualPlatformCost + annualAnalystLicenseCost;

  const netAnnualSavings = annualSavings - totalAnnualCost;
  const roiPercentage = ((netAnnualSavings / totalAnnualCost) * 100).toFixed(0);
  const paybackMonths = (totalAnnualCost / monthlySavings).toFixed(1);

  // Lista de funcionalidades
  const functionalityList = [
    { key: 'incidents', label: 'Incidentes' },
    { key: 'requests', label: 'Solicitação' },
    { key: 'problems', label: 'Problema' },
    { key: 'changes', label: 'Mudanças' },
    { key: 'assetManagement', label: 'Gestão de Ativos/Software' },
    { key: 'knowledgeBase', label: 'Base de Conhecimento' },
    { key: 'reports', label: 'Relatórios' },
    { key: 'forms', label: 'Formulários' },
    { key: 'slaManagement', label: 'Gestão de SLA' }
  ];

  // Lista de módulos AROAN disponíveis
  const aroaneModulesList = [
    { key: 'incidents', label: 'Incidentes', price: 50 },
    { key: 'requests', label: 'Solicitação', price: 60 },
    { key: 'problems', label: 'Problema', price: 50 },
    { key: 'changes', label: 'Mudanças', price: 100 },
    { key: 'assetManagement', label: 'Gestão de Ativos/Software', price: 70 },
    { key: 'knowledgeBase', label: 'Base de Conhecimento', price: 50 },
    { key: 'reports', label: 'Relatórios', price: 0, badge: 'Grátis' },
    { key: 'forms', label: 'Formulários', price: 120 },
    { key: 'slaManagement', label: 'Gestão de SLA', price: 0, badge: 'Grátis' },
    { key: 'aiIntegrated', label: 'IA Integrado', price: 4000, badge: 'Premium' }
  ];

  const handleFeatureChange = (featureKey: string, checked: boolean) => {
    setUserFeatures(prev => ({
      ...prev,
      [featureKey]: checked
    }));
  };

  const handleModuleChange = (moduleKey: string, checked: boolean) => {
    setSelectedModules(prev => ({
      ...prev,
      [moduleKey]: checked
    }));
  };

  const handleEmailFormChange = (field: string, value: string) => {
    setEmailForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmailFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar configuração do webhook
    if (!WEBHOOK_URL || !WEBHOOK_USERNAME || !WEBHOOK_PASSWORD) {
      toast({
        title: "Erro de configuração",
        description: "O webhook da calculadora de ROI não está configurado corretamente.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    const payload = {
      timestamp: new Date().toISOString(),
      source: "aroan_landing_page",
      type: "roi_calculator",
      
      // Dados de contato
      contact: {
        firstName: emailForm.firstName,
        lastName: emailForm.lastName,
        email: emailForm.email,
        company: emailForm.company,
        companySize: emailForm.companySize
      },
      
      // Dados da operação (Página 1)
      operationData: {
        employees,
        ticketsPerMonth,
        avgHandlingTime,
        costPerHour,
        analystLicenseCost,
        isOnPremises
      },
      
      // Funcionalidades atuais do cliente (Página 2)
      currentFeatures: userFeatures,
      
      // Módulos AROAN selecionados (Página 3)
      selectedModules,
      
      // Cálculos de ROI
      calculations: {
        // === SITUAÇÃO ATUAL (SEM AROAN) ===
        current: {
          monthlyOperationalCost: currentMonthlyCost,
          monthlyAnalystLicenses: monthlyAnalystLicenseCost,
          monthlyOnPremisesCost: monthlyOnPremisesCost,
          totalMonthlySpending: totalCurrentMonthlyCost,
          totalAnnualSpending: totalCurrentMonthlyCost * 12,
          monthlyHours: currentMonthlyHours,
          description: `Cliente gasta atualmente R$ ${totalCurrentMonthlyCost.toLocaleString('pt-BR')} por mês (R$ ${(totalCurrentMonthlyCost * 12).toLocaleString('pt-BR')} por ano) com sua operação atual`
        },
        
        // === COM AROAN (NOVA SITUAÇÃO) ===
        withAroan: {
          monthlyOperationalCost: newMonthlyCost,
          monthlyAroaneSubscription: monthlyPlatformCost,
          monthlyAnalystLicenses: monthlyAnalystLicenseCost,
          totalMonthlyPayment: newMonthlyCost + monthlyPlatformCost + monthlyAnalystLicenseCost,
          totalAnnualPayment: (newMonthlyCost + monthlyPlatformCost + monthlyAnalystLicenseCost) * 12,
          monthlyHours: newMonthlyHours,
          description: `Com AROAN, cliente pagará R$ ${(newMonthlyCost + monthlyPlatformCost + monthlyAnalystLicenseCost).toLocaleString('pt-BR')} por mês (R$ ${((newMonthlyCost + monthlyPlatformCost + monthlyAnalystLicenseCost) * 12).toLocaleString('pt-BR')} por ano)`
        },
        
        // === ECONOMIA E ROI ===
        savings: {
          monthlyOperationalSavings: monthlySavings,
          annualOperationalSavings: annualSavings,
          netMonthlySavings: totalCurrentMonthlyCost - (newMonthlyCost + monthlyPlatformCost + monthlyAnalystLicenseCost),
          netAnnualSavings: netAnnualSavings,
          roiPercentage: roiPercentage,
          paybackMonths: paybackMonths,
          description: `Economia líquida: R$ ${(totalCurrentMonthlyCost - (newMonthlyCost + monthlyPlatformCost + monthlyAnalystLicenseCost)).toLocaleString('pt-BR')} por mês | ROI: ${roiPercentage}% | Payback: ${paybackMonths} meses`
        },
        
        // === DETALHAMENTO DOS CUSTOS AROAN ===
        aroaneInvestment: {
          moduleUnitCost,
          monthlyPlatformCost,
          annualPlatformCost,
          implementationType: isOnPremises ? 'On-Premises' : 'Cloud',
          selectedModulesCount: Object.values(selectedModules).filter(Boolean).length,
          description: `Investimento mensal no AROAN: R$ ${monthlyPlatformCost.toLocaleString('pt-BR')} (R$ ${annualPlatformCost.toLocaleString('pt-BR')} anuais) para ${employees} usuários`
        },
        
        // === RESUMO EXECUTIVO ===
        executiveSummary: {
          currentSituation: `Gasto atual: R$ ${totalCurrentMonthlyCost.toLocaleString('pt-BR')}/mês`,
          futureWithAroan: `Com AROAN: R$ ${(newMonthlyCost + monthlyPlatformCost + monthlyAnalystLicenseCost).toLocaleString('pt-BR')}/mês`,
          netBenefit: `Economia: R$ ${(totalCurrentMonthlyCost - (newMonthlyCost + monthlyPlatformCost + monthlyAnalystLicenseCost)).toLocaleString('pt-BR')}/mês`,
          roi: `ROI: ${roiPercentage}%`,
          payback: `Payback: ${paybackMonths} meses`
        }
      },
      
      // === VARIÁVEIS ADICIONAIS PARA O EMAIL ===
      withAroaneMonthlyTotal: newMonthlyCost + monthlyPlatformCost + monthlyAnalystLicenseCost,
      netMonthlySavingsCalculated: totalCurrentMonthlyCost - (newMonthlyCost + monthlyPlatformCost + monthlyAnalystLicenseCost),
      
      // === ÍCONES DOS MÓDULOS ===
      selectedModules_incidents_icon: selectedModules.incidents ? '✅' : '❌',
      selectedModules_requests_icon: selectedModules.requests ? '✅' : '❌',
      selectedModules_problems_icon: selectedModules.problems ? '✅' : '❌',
      selectedModules_changes_icon: selectedModules.changes ? '✅' : '❌',
      selectedModules_assetManagement_icon: selectedModules.assetManagement ? '✅' : '❌',
      selectedModules_knowledgeBase_icon: selectedModules.knowledgeBase ? '✅' : '❌',
      selectedModules_reports_icon: selectedModules.reports ? '✅' : '❌',
      selectedModules_forms_icon: selectedModules.forms ? '✅' : '❌',
      selectedModules_slaManagement_icon: selectedModules.slaManagement ? '✅' : '❌',
      selectedModules_aiIntegrated_icon: selectedModules.aiIntegrated ? '✅' : '❌'
    };

    try {
      const credentials = btoa(`${WEBHOOK_USERNAME}:${WEBHOOK_PASSWORD}`);
      const response = await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Basic ${credentials}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      toast({
        title: "Sucesso!",
        description: "Seu cálculo de ROI será enviado para o email informado em breve.",
      });

      // Resetar formulário e fechar modal
      setEmailForm({ firstName: '', lastName: '', email: '', company: '', companySize: '' });
      setShowEmailForm(false);
      onOpenChange(false);
    } catch (error) {
      console.error("Erro ao enviar dados:", error);
      toast({
        title: "Erro ao enviar",
        description: "Não foi possível enviar seus dados. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextPage = () => {
    if (currentPage < 3) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <Calculator className="w-6 h-6 text-primary" />
            Calculadora de ROI Personalizada - Página {currentPage} de 3
          </DialogTitle>
          <DialogDescription>
            {currentPage === 1 
              ? "Insira os dados da sua operação atual"
              : currentPage === 2
              ? "Compare suas funcionalidades atuais com o AROAN"
              : "Selecione os módulos AROAN que deseja contratar"
            }
          </DialogDescription>
        </DialogHeader>

        {currentPage === 1 && (
          <div className="space-y-6 mt-6">
            <h3 className="text-lg font-semibold mb-4">Dados da Operação</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="employees" className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  Quantidade de Resolvedores na Ferramenta
                </Label>
                <Input
                  id="employees"
                  type="number"
                  value={employees}
                  onChange={(e) => setEmployees(Number(e.target.value))}
                  min="1"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tickets" className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-primary" />
                  Tickets/Chamados por Mês
                </Label>
                <Input
                  id="tickets"
                  type="number"
                  value={ticketsPerMonth}
                  onChange={(e) => setTicketsPerMonth(Number(e.target.value))}
                  min="1"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="time" className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary" />
                  Tempo Médio de Atendimento (minutos)
                </Label>
                <Input
                  id="time"
                  type="number"
                  value={avgHandlingTime}
                  onChange={(e) => setAvgHandlingTime(Number(e.target.value))}
                  min="1"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cost" className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-primary" />
                  Custo Médio da Hora (R$)
                </Label>
                <Input
                  id="cost"
                  type="number"
                  value={costPerHour}
                  onChange={(e) => setCostPerHour(Number(e.target.value))}
                  min="1"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="analystLicense" className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-primary" />
                  Valor pago por licença de Analista (R$)
                </Label>
                <Input
                  id="analystLicense"
                  type="number"
                  value={analystLicenseCost}
                  onChange={(e) => setAnalystLicenseCost(Number(e.target.value))}
                  min="0"
                  required
                />
              </div>
            </div>

            <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl p-6">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold">Custo Total Medio por Mês</span>
                <span className="text-3xl font-bold text-primary">
                  R$ {totalCurrentMonthlyCost.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </span>
              </div>
            </div>

            <div className="space-y-4">
              <Label className="text-base font-semibold">Tipo de Implementação</Label>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="onPremises"
                  checked={isOnPremises}
                  onCheckedChange={(checked) => setIsOnPremises(checked as boolean)}
                />
                <Label htmlFor="onPremises" className="text-sm font-normal">
                  On Premises (Sim / Não)
                </Label>
              </div>
              <p className="text-sm text-muted-foreground">
                {isOnPremises 
                  ? ""
                  : ""
                }
              </p>
            </div>
          </div>
        )}

        {currentPage === 2 && (
          <div className="space-y-6 mt-6">
            <h3 className="text-lg font-semibold mb-4">Comparação de Funcionalidades</h3>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-border rounded-lg">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="border border-border p-4 text-left font-semibold">Funcionalidade</th>
                    <th className="border border-border p-4 text-center font-semibold">Sua Ferramenta</th>
                    <th className="border border-border p-4 text-center font-semibold">AROAN</th>
                  </tr>
                </thead>
                <tbody>
                  {functionalityList.map((functionality) => (
                    <tr key={functionality.key} className="hover:bg-muted/25">
                      <td className="border border-border p-4">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={functionality.key}
                            checked={userFeatures[functionality.key as keyof typeof userFeatures]}
                            onCheckedChange={(checked) => handleFeatureChange(functionality.key, checked as boolean)}
                          />
                          <Label htmlFor={functionality.key} className="cursor-pointer">
                            {functionality.label}
                          </Label>
                        </div>
                      </td>
                      <td className="border border-border p-4 text-center">
                        {userFeatures[functionality.key as keyof typeof userFeatures] ? (
                          <Check className="w-5 h-5 text-success mx-auto" />
                        ) : (
                          <X className="w-5 h-5 text-destructive mx-auto" />
                        )}
                      </td>
                      <td className="border border-border p-4 text-center">
                        <Check className="w-5 h-5 text-success mx-auto" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {currentPage === 3 && (
          <div className="space-y-6 mt-6">
            <h3 className="text-lg font-semibold mb-4">Quais Módulos do AROAN você pensa em contratar?</h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              {aroaneModulesList.map((module) => (
                <div key={module.key} className="border border-border rounded-lg p-4 hover:bg-muted/25">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id={`module-${module.key}`}
                        checked={selectedModules[module.key as keyof typeof selectedModules]}
                        onCheckedChange={(checked) => handleModuleChange(module.key, checked as boolean)}
                      />
                      <div>
                        <Label htmlFor={`module-${module.key}`} className="cursor-pointer font-medium">
                          {module.label}
                        </Label>
                        {module.badge && (
                          <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                            module.badge === 'Grátis' ? 'bg-success/20 text-success' : 'bg-primary/20 text-primary'
                          }`}>
                            {module.badge}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl p-6 mt-6 text-center">
              <h4 className="text-xl font-semibold mb-4">Cálculo Personalizado Pronto!</h4>
              <p className="text-muted-foreground mb-6">
                Seu cálculo de ROI personalizado está pronto com base nos módulos selecionados.
                Receba o relatório completo no seu email.
              </p>
              <Button 
                onClick={() => setShowEmailForm(true)}
                size="lg"
                className="w-full md:w-auto"
              >
                Receber Cálculo de ROI no seu Email
              </Button>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mt-6">
          <Button 
            variant="outline" 
            onClick={prevPage}
            disabled={currentPage === 1}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Anterior
          </Button>
          
          <div className="flex gap-2">
            <div className={`w-2 h-2 rounded-full ${
              currentPage === 1 ? 'bg-primary' : 'bg-muted'
            }`} />
            <div className={`w-2 h-2 rounded-full ${
              currentPage === 2 ? 'bg-primary' : 'bg-muted'
            }`} />
            <div className={`w-2 h-2 rounded-full ${
              currentPage === 3 ? 'bg-primary' : 'bg-muted'
            }`} />
          </div>

          {currentPage < 3 && (
            <Button onClick={nextPage} className="flex items-center gap-2">
              Próximo
              <ChevronRight className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Modal do Formulário de Email */}
        {showEmailForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-background rounded-lg p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold mb-4">Receber Cálculo de ROI</h3>
              <form onSubmit={handleEmailFormSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">Nome</Label>
                    <Input
                      id="firstName"
                      type="text"
                      value={emailForm.firstName}
                      onChange={(e) => handleEmailFormChange('firstName', e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Sobrenome</Label>
                    <Input
                      id="lastName"
                      type="text"
                      value={emailForm.lastName}
                      onChange={(e) => handleEmailFormChange('lastName', e.target.value)}
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email Corporativo</Label>
                  <Input
                    id="email"
                    type="email"
                    value={emailForm.email}
                    onChange={(e) => handleEmailFormChange('email', e.target.value)}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="company">Nome da Empresa</Label>
                  <Input
                    id="company"
                    type="text"
                    value={emailForm.company}
                    onChange={(e) => handleEmailFormChange('company', e.target.value)}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="companySize">Quantidade de Funcionários</Label>
                  <Select
                    value={emailForm.companySize}
                    onValueChange={(value) => handleEmailFormChange('companySize', value)}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tamanho da empresa" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0-50">0-50 funcionários</SelectItem>
                      <SelectItem value="51-100">51-100 funcionários</SelectItem>
                      <SelectItem value="101-200">101-200 funcionários</SelectItem>
                      <SelectItem value="201-500">201-500 funcionários</SelectItem>
                      <SelectItem value="500+">Mais de 500 funcionários</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      "Enviar"
                    )}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowEmailForm(false)}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ROICalculator;
